/** GraphQL client for api.unboxugc.com. The API is on another origin, so the ID token is attached by hand. */

export const API_URL = import.meta.env.VITE_API_URL || 'https://api.unboxugc.com';
const RETRY_HEADER = 'X-Shopify-Retry-Invalid-Session-Request';

export class ApiError extends Error {
  constructor(message: string, readonly status: number) {
    super(message);
  }
}

/** `fresh` asks for a new token instead of the cached one (the API said the last one was no longer valid). */
type TokenSource = (fresh?: boolean) => Promise<string>;
type Fetcher = typeof fetch;

// ID tokens live about a minute. Getting one from Shopify sometimes takes seconds, and every page change starts
// several requests at once, so one token is shared: reused while it has more than REFRESH_S left, renewed in the
// background after that, and only waited for when it is within EXPIRY_MARGIN_S of expiring (or there is none yet).
const REFRESH_S = 30;
const EXPIRY_MARGIN_S = 10;

export function expiresAt(token: string): number {
  try {
    const payload = token.split('.')[1] ?? '';
    const json = JSON.parse(atob(payload.replace(/-/g, '+').replace(/_/g, '/'))) as { exp?: unknown };
    return typeof json.exp === 'number' ? json.exp : 0;
  } catch {
    return 0;
  }
}

export function tokenCache(source: () => Promise<string>, now = () => Date.now() / 1000): TokenSource {
  let cached: { token: string; exp: number } | null = null;
  let pending: Promise<string> | null = null;
  const renew = () => {
    pending ??= source()
      .then(token => {
        cached = { token, exp: expiresAt(token) };
        return token;
      })
      .finally(() => {
        pending = null;
      });
    return pending;
  };
  return async (fresh = false) => {
    if (fresh) cached = null;
    const left = cached ? cached.exp - now() : 0;
    if (cached && left > REFRESH_S) return cached.token;
    if (cached && left > EXPIRY_MARGIN_S) {
      renew().catch(() => undefined);
      return cached.token;
    }
    return renew();
  };
}

const defaultToken = tokenCache(() => shopify.idToken());

/**
 * POSTs a GraphQL operation. A 401 with the retry header means the ID token expired between issue and use
 * (they live about a minute), so it asks for a fresh token and retries once.
 */
export async function graphql<T>(
  query: string,
  variables: Record<string, unknown> = {},
  { token = defaultToken, fetcher = fetch }: { token?: TokenSource; fetcher?: Fetcher } = {},
): Promise<T> {
  for (let attempt = 0; attempt < 2; attempt++) {
    const response = await fetcher(`${API_URL}/graphql/admin`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${await token(attempt > 0)}` },
      body: JSON.stringify({ query, variables }),
    });
    if (response.status === 401 && response.headers.get(RETRY_HEADER) && attempt === 0) continue;
    if (!response.ok) throw new ApiError(`API responded ${response.status}`, response.status);
    const body = (await response.json()) as { data?: T; errors?: { message: string }[] };
    if (body.errors?.length) throw new ApiError(body.errors.map(e => e.message).join('; '), 200);
    if (!body.data) throw new ApiError('Empty response', 200);
    return body.data;
  }
  throw new ApiError('Session expired', 401);
}

export type ShopInfo = {
  shop: {
    id: string;
    domain: string;
    name: string;
    plan: { code: string; name: string };
    isTestStore: boolean;
    entitlements: { layouts?: string[]; video_autoplay?: boolean; max_feeds?: number | null; max_tagged_posts?: number | null; page_filter?: boolean; analytics?: boolean; file_uploads?: boolean; mobile_layout?: boolean; ugc_moderation?: boolean; comment_inbox?: boolean };
  };
  app: { version: string; sha: string };
  setup: { done: boolean };
};

export const SHOP_QUERY = `{ shop { id domain name isTestStore plan { code name } entitlements } app { version sha } setup { done } }`;
